import { clsx } from 'clsx'
import { useContext } from 'react'

import { useBrowserService } from '@lib/hooks'

import { type TypeSectionHeroEntry } from '@contentful-types/section-hero'

import { CtaDescriptionPosition, CtaDescriptionWidth } from '@constants/enums/cta'
import {
  SingleColBeforeBreakpoints,
  TwoColumnGapSize,
  TwoColumnRatio,
} from '@constants/enums/layout'
import { SectionHeroLayoutDirection } from '@constants/enums/page-section'

import { AlignmentContext, DefaultAlignment } from '@contexts/alignment-context'
import { CtaButtonMetadataProvider, DefaultCtaButtonContext } from '@contexts/cta-button-context'
import { SectionContext } from '@contexts/section-context'

import { SingleColumnLayout } from '@components/layout/single-column/single-column'
import { TwoColumnLayout } from '@components/layout/two-column/two-column'
import { PageSection } from '@components/page-core/section/page-section'
import { ContentRenderer } from '@components/renderer/base'
import { HeroTitle } from '@components/section/hero/hero-title'

import { FixedSpacer } from '../../fixed-spacer/fixed-spacer'

import styles from './hero.module.scss'

const HeroSectionContent = ({ data }: { data: TypeSectionHeroEntry }) => {
  const {
    layoutDirection,
    image,
    // dynamicRenderAs
  } = data?.fields

  const isVertical = layoutDirection === SectionHeroLayoutDirection.Vertical
  const isVerticalWide = layoutDirection === SectionHeroLayoutDirection.VerticalWide
  const isLayoutReversed = layoutDirection === SectionHeroLayoutDirection.Reversed
  const { isMobile } = useBrowserService()
  const { isGrouped } = useContext(SectionContext)

  const content = (
    <CtaButtonMetadataProvider
      value={{
        ...DefaultCtaButtonContext,
        descriptionPosition: CtaDescriptionPosition.Left,
        descriptionWidth: CtaDescriptionWidth.Auto,
        showCtaDescription: false,
      }}
    >
      {isVertical || isVerticalWide ? (
        <SingleColumnLayout
          className={clsx({
            [styles.singleSectionWide]: isVerticalWide,
            [styles.singleSectionWideGrouped]: isGrouped && isVerticalWide,
          })}
        >
          <AlignmentContext.Provider value={DefaultAlignment}>
            <HeroTitle data={data} />
            {!(isVerticalWide && isMobile) && <FixedSpacer multiplier={isMobile ? 3 : 5} />}
            <ContentRenderer data={image} />
          </AlignmentContext.Provider>
        </SingleColumnLayout>
      ) : (
        <TwoColumnLayout
          leftContent={
            isLayoutReversed ? <ContentRenderer data={image} /> : <HeroTitle data={data} />
          }
          rightContent={
            isLayoutReversed ? <HeroTitle data={data} /> : <ContentRenderer data={image} />
          }
          columnRatio={TwoColumnRatio.OneToOne}
          gap={TwoColumnGapSize.M}
          singleColBefore={SingleColBeforeBreakpoints.Medium}
          className={styles.twoColRedesign24}
        />
      )}
    </CtaButtonMetadataProvider>
  )

  return content
}

export const HeroSection = ({
  className,
  data,
}: {
  className?: string
  data: TypeSectionHeroEntry
}) => {
  return (
    <PageSection
      internalName={data?.fields?.internalName}
      sysStructure={data?.sys}
      className={className}
      background={data?.fields?.background}
    >
      <HeroSectionContent data={data} />
    </PageSection>
  )
}
